import React from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  HStack,
  Progress,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import AnimatedBox from './AnimatedBox';

const STATUS_COPY = {
  pending: {
    color: 'blue',
    heading: 'Setting up your market',
    body: "We're preparing your market analysis — reviews, ratings, and competitor data. This usually takes about 2.5 hours. We'll email you when it's ready.",
    showProgress: true,
  },
  scraping: {
    color: 'blue',
    heading: 'Building your dashboard',
    body: "We're collecting reviews, ratings, and competitive data for your market. This usually takes about 2.5 hours — we'll email you when it's ready.",
    showProgress: true,
  },
  delayed: {
    color: 'orange',
    heading: 'This is taking longer than usual',
    body: "Your market data collection is still running — some markets just take longer. No action needed; we'll email you the moment your dashboard is ready.",
    showProgress: true,
  },
  failed: {
    color: 'red',
    heading: 'We hit a snag building your dashboard',
    body: 'Something went wrong while collecting your market data. Our team has been notified — reach out and we will get you sorted right away.',
    showProgress: false,
  },
};

export default function OnboardingStatusPanel({ status, businesses = [] }) {
  const copy = STATUS_COPY[status];
  if (!copy) return null;

  const businessCount = businesses.length;
  const topBusinesses = [...businesses]
    .sort((a, b) => (b.rating_count || 0) - (a.rating_count || 0))
    .slice(0, 10);

  return (
    <Box
      p={{ base: 4, md: 8 }}
      maxW="900px"
      mx="auto"
      data-testid="onboarding-status-panel"
    >
      <AnimatedBox animation='fadeIn' duration={0.6}>
        <Box
          bg={`${copy.color}.50`}
          border="1px solid"
          borderColor={`${copy.color}.200`}
          borderRadius="xl"
          p={6}
          mb={8}
          textAlign="center"
        >
          <Heading size="lg" color={`${copy.color}.700`} mb={2}>
            {copy.heading}
          </Heading>
          <Text color={`${copy.color}.600`} fontSize="md" mb={4}>
            {copy.body}
          </Text>
          {copy.showProgress && (
            <Progress
              size="sm"
              isIndeterminate
              colorScheme={copy.color}
              borderRadius="full"
              mx="auto"
              maxW="400px"
            />
          )}
          {status === 'failed' && (
            <Button
              as="a"
              href="mailto:help@localytics.co"
              colorScheme="red"
              variant="outline"
              size="sm"
            >
              help@localytics.co
            </Button>
          )}
        </Box>

        {businessCount > 0 && (
          <Box>
            <Heading size="md" mb={1} color="gray.700">
              Here's what we've found so far
            </Heading>
            <Text color="gray.500" fontSize="sm" mb={4}>
              {businessCount} businesses identified in your local market
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              {topBusinesses.map((biz, i) => (
                <Card key={i} variant="outline" size="sm">
                  <CardBody py={3} px={4}>
                    <Flex align="center" justify="space-between">
                      <Box>
                        <Text fontWeight="600" fontSize="sm" noOfLines={1}>
                          {biz.name || 'Unknown Business'}
                        </Text>
                        <HStack spacing={3} mt={1}>
                          {biz.rating_avg && (
                            <Text fontSize="xs" color="gray.500">
                              {biz.rating_avg.toFixed(1)} rating
                            </Text>
                          )}
                          {biz.rating_count && (
                            <Text fontSize="xs" color="gray.500">
                              {biz.rating_count.toLocaleString()} reviews
                            </Text>
                          )}
                        </HStack>
                      </Box>
                      <Badge
                        colorScheme="green"
                        fontSize="xs"
                        borderRadius="full"
                        px={2}
                      >
                        #{i + 1}
                      </Badge>
                    </Flex>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </Box>
        )}
      </AnimatedBox>
    </Box>
  );
}
